import H3Layer from "@/layers/H3Layer";
import PointLayer from "@/layers/PointLayer";
import TripsLayer from "@/layers/TripsLayer";
import TextLayer from "@/layers/TextLayer";
import DistanceToolLayer from "@/layers/DistanceToolLayer";
import { getCities, mapLatLongToCity } from "@/api/cities";
import City from "@/models/City.model";
import notify from "@/lib/notifications";
import { types } from "./mutations";

export default {
  /**
   * Loads geo data into the state
   */
  loadGeoData: async ({ commit }, data) => {
    commit(types.SET_GEO_DATA, data);
  },

  /**
   * Sets Map Styles
   */
  setMapStyle: async ({ commit }, map_style) => {
    return new Promise(resolve => {
      commit(types.SET_MAP_STYLE, map_style);
      return resolve();
    });
  },

  /**
   * Fetches the available cities from the API and stores
   */
  fetchCities: async ({ commit, state }, entity_id) => {
    if (state.cities && state.cities.length > 0) return state.cities;

    try {
      commit(types.SET_FETCHING_CITIES, true);
      const {
        data: { data: cities },
      } = await getCities(entity_id);
      if (cities && cities.length > 0) {
        commit(
          types.SET_CITIES,
          cities.map(data => new City(data))
        );
        commit(types.SET_FETCHING_CITIES, false);
      } else {
        commit(types.SET_FETCHING_CITIES, false);
        throw new Error("No cities found");
      }
    } catch (error) {
      commit(types.SET_FETCHING_CITIES, false);
      console.error(error);
    }
  },

  /**
   * Fetch city details for latlong
   */
  fetchCityForLatLong: async ({}, latlong) => {
    try {
      const response = await mapLatLongToCity(latlong);
      const { status, data } = response;
      if (status === 200 && data) {
        return data.data;
      } else {
        throw new Error("No city data");
      }
    } catch (error) {
      console.error(error);
      notify.warn("Unable to find city data for this location");
      return null;
    }
  },

  /**
   * Sets the layer data into the DeckGL object
   */
  setLayerData: async (
    { commit, dispatch, state },
    { layerData, viewState, staticLayerData }
  ) => {
    return new Promise(async (resolve, reject) => {
      // generate new grid layer with the data and filter applied
      let gridFilter = state.gridFilter;
      // if there is a filter applied to the type
      if (layerData.type) {
        // find the filter and set it to the layer
        const filter = state.mapFilters.find(
          item => item.type === layerData.type
        );
        if (filter) {
          gridFilter = filter;
        }
      }

      function setPopupData({ show, title, id, value, x, y }) {
        commit("SET_POPUP_DATA", {
          y,
          x,
          show,
          grid_id: id,
          title,
          value,
        });
      }

      function onGridClicked(data) {
        if (data.grid_id) {
          commit(types.SET_ACTIVE_GRID, data.grid_id);
          dispatch("Popup/toggleGridLookupPopup", null, { root: true });
          notify.success("Lookup updated for Grid_Id", data.grid_id);
        }
      }

      try {
        let overlays = staticLayerData
          ? staticLayerData
              .map(item => {
                return [
                  new PointLayer(
                    item.id,
                    item.entities,
                    item.name,
                    setPopupData
                  ).getLayer(),

                  new TextLayer({
                    layer_id: item.id,
                    data: item.entities,
                  }).getLayer(),
                ];
              })
              .flat()
          : null;

        let percentages = [];

        if (state.showMeasureTool) {
          const measureDistance = new DistanceToolLayer({
            layer_id: "MeasureDistance",
            data: state.measureData,
            updateState: value => {
              commit("SET_MEASURE_DATA", value);
            },
          });
          const distanceLayer = measureDistance.getLayer();
          overlays.push(distanceLayer);
        }

        if (layerData.layer_type === "base") {
          const h3Layer = new H3Layer({
            layer_id: layerData.layer_id,
            data: layerData.data,
            gridFilter: gridFilter,
            opacity: state.opacity,
            setPopupData: setPopupData,
            onGridClicked: onGridClicked,
          });

          const gridLayer = h3Layer.getLayer();
          percentages = h3Layer.percentages;
          let layers = [];
          if (layerData.data && layerData.data.length > 0) {
            layers.push(gridLayer);
          } else {
            notify.studio(
              "No GeoData",
              "The selected layer doesnt seem to have any geo data for the selected time."
            );
          }
          layers.push(...overlays);
          layerData.deck.setProps({
            layers,
            viewState: {
              latitude: viewState.latitude,
              longitude: viewState.longitude,
              zoom: viewState.zoom,
              pitch: viewState.pitch,
              bearing: viewState.bearing,
            },
          });

          resolve(layers);
        } else if (layerData.layer_type === "trip") {
          const tripsLayer = new TripsLayer(
            layerData.layer_type,
            layerData.data,
            gridFilter,
            setPopupData
          );
          const pathLayer = tripsLayer.getLayer();
          percentages = tripsLayer.percentages;
          let layers = [];
          if (layerData.data && layerData.data.length > 0) {
            layers.push(pathLayer);
          } else {
            notify.studio(
              "No GeoData",
              "The selected layer doesnt seem to have any geo data for the selected time."
            );
          }
          layers.push(...overlays);
          layerData.deck.setProps({
            layers,
            viewState: {
              latitude: viewState.latitude,
              longitude: viewState.longitude,
              zoom: viewState.zoom,
              pitch: viewState.pitch,
              bearing: viewState.bearing,
            },
          });
          resolve(layers);
        } else {
          layerData.deck.setProps({
            layers: overlays,
            viewState: {
              latitude: viewState.latitude,
              longitude: viewState.longitude,
              zoom: viewState.zoom,
              pitch: viewState.pitch,
              bearing: viewState.bearing,
            },
          });
          resolve(overlays);
        }

        // get percentages from the layer
        if (layerData.type) {
          commit("SET_MAP_LEGENDS", {
            type: layerData.type,
            legend: percentages,
          });
        } else {
          commit("SET_LEGEND", percentages);
        }
      } catch (error) {
        console.error(error);
      }
    });
  },

  /**
   * Returns the active city filter
   */
  getCityFilterPayload: async ({ state }) => {
    if (state.activeCity && state.activeCity.id) {
      return {
        colname: "__city_id",
        comparison: "=",
        value: state.activeCity.id,
      };
    } else {
      return null;
    }
  },

  /**
   * Sets the zoom level
   */
  setZoom: async ({ commit }, value) => {
    commit(types.SET_ZOOM, value);
  },

  /**
   * Increases the zoom level
   */
  zoomIn: async ({ commit, state }) => {
    if (state.viewState.zoom) {
      const newState =
        state.viewState.zoom + 1 > 20 ? 20 : state.viewState.zoom + 1;
      commit(types.SET_ZOOM, newState);
    }
  },

  /**
   * Decreases the zoom level
   */
  zoomOut: async ({ commit, state }) => {
    if (state.viewState.zoom) {
      const newState =
        state.viewState.zoom - 1 < 0 ? 0 : state.viewState.zoom - 1;
      commit(types.SET_ZOOM, newState);
    }
  },

  /**
   * Sets opacity of the visualization
   */
  setOpacity: async ({ commit }, { opacity }) => {
    commit(types.SET_OPACITY, opacity);
  },

  /**
   * Tilts the MAP
   */
  toggleTilt: async ({ commit, state }) => {
    commit(types.SET_TILT, !state.isTilted);
  },

  /**
   * Sets the legend filter
   */
  setGridFilter: async ({ commit }, filter) => {
    if (filter.type) {
      commit(types.SET_MAP_FILTER, filter);
    } else {
      commit(types.SET_GRID_FILTER, filter);
    }
  },

  /**
   * Sets the viewState with a viewstate object
   */
  setViewState: async ({ commit, state }, data) => {
    let newState = {
      ...state.viewState,
      ...data,
    };

    commit(types.SET_VIEW_STATE, newState);
    return newState;
  },

  /**
   * Sets the active city
   */
  setActiveCity: async ({ state, commit }, city) => {
    if (!(state.activeCity && state.activeCity.city_id !== city.city_id))
      commit(types.SET_ACTIVE_CITY, city);
  },

  /**
   *  Toggle the overlays for the Map
   */
  toggleOverlay: async ({ commit, state }, overlay) => {
    commit(
      types.SET_OVERLAYS,
      state.overlays.map(item => {
        if (item.id === overlay.id) {
          return {
            ...item,
            isVisible: !item.isVisible,
          };
        } else {
          return item;
        }
      })
    );
  },

  /**
   * Sets the active jump location on the map
   */
  setLocation: async ({ commit }, latlong) => {
    commit(types.SET_ACTIVE_LOCATION, latlong);
  },

  /**
   * Sets the cotext data required for the popup
   */
  setContextData: async ({ commit }, data) => {
    commit(types.SET_CONTEXT_DATA, data);
  },

  /**
   * Activates the measure tool
   */
  toggleMeasureTool: async ({ commit, state }) => {
    commit(types.SET_SHOW_MEASURE_TOOL, !state.showMeasureTool);
  },

  /**
   * Sets the current selected grid
   */
  setActiveGrid: async ({ commit }, grid_id) => {
    commit(types.SET_ACTIVE_GRID, grid_id);
  },

  /**
   * Resets the module with initial state
   */
  reset: async ({ commit }) => {
    commit(types.reset);
  },
};
